import React, { useState } from 'react';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { Page } from '../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { getAdjustedBreadcrumbPathItems } from '@shared/services/breadcrumbService';
import { userInfo } from '@apollo-internal/cache';
import { useReactiveVar } from '@apollo/client';
import DataCorrelationReportFilters from './DataCorrelationReportFilters';
import ApolloError from '@shared/components/ApolloError';
import useClassificationMetricList from '@shared/hooks/useClassificationMetricList';
import useMddapPracticeAreas from '@shared/hooks/useMddapPracticeAreas';
import useDataCorrelationReport from '../hooks/useDataCorrelationReport';
import DataCorrelationGraphsList from './DataCorrelationGraphsList';

const TITLE = 'Data Correlations';
const DESCRIPTION =
  'Correlations between changes in metric data sets and changes appraisal data sets over certain periods of time. ' +
  'Note: correlation does not necessarily mean causation.';

const BREADCRUMB_PATH_ITEMS = [
  { name: 'Data Reports', url: '/data-reports' },
  {
    name: 'Industry Reports',
    url: '/data-reports/industry-reports'
  },
  {
    name: TITLE,
    url: ''
  }
];

const DataCorrelationsPage = () => {
  const currentUserInfo = useReactiveVar(userInfo);

  const [selectedFilter, setSelectedFilter] = useState({
    practiceAreaId: null,
    classificationMetricId: null
  });

  const adjustedBreadcrumbPathItems = getAdjustedBreadcrumbPathItems(
    BREADCRUMB_PATH_ITEMS,
    currentUserInfo.SystemRole.Id
  );

  const {
    data: practiceAreas,
    loading: practiceAreasLoading,
    error: practiceAreasError
  } = useMddapPracticeAreas();

  const {
    classificationMetrics,
    classificationMetricsLoading,
    error: classificationMetricsError
  } = useClassificationMetricList();

  const selectedPracticeArea = practiceAreas?.find(
    pa => pa.InternalId === selectedFilter.practiceAreaId
  );
  const selectedClassificationMetric = classificationMetrics?.find(
    metric => metric.id === selectedFilter.classificationMetricId
  );

  const {
    mergedReportData,
    isLoading,
    fetchMoreData,
    totalGraphCount,
    error: reportError
  } = useDataCorrelationReport(selectedFilter, selectedPracticeArea);

  if (practiceAreasError || classificationMetricsError || reportError) {
    return <ApolloError />;
  }

  return (
    <Page title={TITLE} className='data-correlations-page'>
      <Breadcrumb pathItems={adjustedBreadcrumbPathItems} />
      <PageInstructions title={TITLE} description={DESCRIPTION} />
      <DataCorrelationReportFilters
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        practiceAreas={practiceAreas}
        classificationMetrics={classificationMetrics}
        isLoading={practiceAreasLoading || classificationMetricsLoading}
      />

      {selectedFilter.practiceAreaId && selectedFilter.classificationMetricId && (
        <DataCorrelationGraphsList
          key={`${selectedFilter.practiceAreaId}-${selectedFilter.classificationMetricId}`}
          mergedReportData={mergedReportData}
          isLoading={isLoading}
          selectedClassificationMetric={selectedClassificationMetric}
          selectedPracticeArea={selectedPracticeArea}
          fetchMoreData={fetchMoreData}
          totalGraphCount={totalGraphCount}
        />
      )}
    </Page>
  );
};

export default DataCorrelationsPage;
